import React, { useCallback, useEffect, useState } from 'react';
import { companyDocsAPI, usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/dateUtils';
import { parseApiError } from '../utils/errors';

const CATEGORIES = ['SOP', 'Policy', 'Handbook', 'Form', 'Training', 'Compliance', 'Other'];
const EMPTY = { title: '', url: '', description: '', category: 'SOP', version: '', doc_kind: '' };

const KINDS = [
  { value: '', label: 'Auto-detect' },
  { value: 'pdf', label: 'PDF' },
  { value: 'doc', label: 'Word document' },
  { value: 'sheet', label: 'Spreadsheet' },
  { value: 'slide', label: 'Presentation' },
  { value: 'folder', label: 'Folder' },
  { value: 'page', label: 'Web page' },
  { value: 'other', label: 'Other' },
];

const KIND_ICONS = {
  pdf: '📕', doc: '📘', sheet: '📗', slide: '📙',
  folder: '📁', page: '🔗', other: '📄',
};

/** Best guess at what a link points to, for picking an icon. */
function guessKind(doc) {
  if (doc.doc_kind) return doc.doc_kind;
  const u = (doc.url || '').toLowerCase();
  if (u.includes('/spreadsheets/') || u.endsWith('.xlsx') || u.endsWith('.xls') || u.endsWith('.csv')) return 'sheet';
  if (u.includes('/presentation/') || u.endsWith('.pptx') || u.endsWith('.ppt')) return 'slide';
  if (u.includes('/folders/') || u.includes('/drive/folders')) return 'folder';
  if (u.endsWith('.pdf')) return 'pdf';
  if (u.includes('/document/') || u.endsWith('.docx') || u.endsWith('.doc')) return 'doc';
  return 'page';
}

/** The readable host, so people can see where a link goes before clicking. */
function hostOf(url) {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
}

/**
 * The organisation document library.
 *
 * Anyone may add a link; it stays private to them and the super admin until the
 * super admin shares it. Editing, removing and sharing are super-admin only.
 * Opening a shared link is one click - no password step.
 */
export default function CompanyDocuments() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin';

  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [sharing, setSharing] = useState(null);   // document whose access is open
  const [shares, setShares] = useState([]);
  const [picked, setPicked] = useState([]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3000); };

  const load = useCallback(() => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (category) params.category = category;
    setLoading(true);
    companyDocsAPI.list(params)
      .then(({ data }) => setDocs(data))
      .catch((e) => setError(parseApiError(e, 'Failed to load documents')))
      .finally(() => setLoading(false));
  }, [search, category]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const loadCategories = useCallback(() => {
    companyDocsAPI.categories().then(({ data }) => setCategories(data)).catch(() => {});
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  useEffect(() => {
    if (isAdmin) usersAPI.list({ is_active: true }).then(({ data }) => setPeople(data)).catch(() => {});
  }, [isAdmin]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (doc) => {
    setEditing(doc);
    setForm({
      title: doc.title, url: doc.url, description: doc.description || '',
      category: doc.category || '', version: doc.version || '',
      doc_kind: doc.doc_kind || '',
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      title: form.title,
      url: form.url.trim(),
      description: form.description || null,
      category: form.category || null,
      version: form.version || null,
      doc_kind: form.doc_kind || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await companyDocsAPI.update(editing.id, payload);
        flash(payload.url !== editing.url ? 'Link updated — people it is shared with were notified' : 'Details updated');
      } else {
        await companyDocsAPI.add(payload);
        flash(isAdmin
          ? 'Document added. Use Share to make it visible to colleagues.'
          : 'Document added. The admin has been notified and will share it.');
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      load();
      loadCategories();
    } catch (err) {
      setError(parseApiError(err, 'Could not save this document'));
    } finally { setSaving(false); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove "${doc.title}" from the library? The linked file itself is not touched.`)) return;
    try { await companyDocsAPI.remove(doc.id); flash('Document removed'); load(); loadCategories(); }
    catch (err) { setError(parseApiError(err, 'Could not remove this document')); }
  };

  const openShare = async (doc) => {
    setSharing(doc);
    setPicked([]);
    try { const { data } = await companyDocsAPI.shares(doc.id); setShares(data); }
    catch (err) { setError(parseApiError(err, 'Could not load the share list')); }
  };

  const togglePick = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const saveShares = async () => {
    if (picked.length === 0) return;
    try {
      const { data } = await companyDocsAPI.share(sharing.id, { user_ids: picked });
      setShares(data);
      setPicked([]);
      flash('Shared');
      load();
    } catch (err) { setError(parseApiError(err, 'Could not share this document')); }
  };

  const shareEveryone = async () => {
    if (!window.confirm(`Share "${sharing.title}" with everyone in the company?`)) return;
    try {
      const { data } = await companyDocsAPI.shareEveryone(sharing.id);
      setShares(data);
      setPicked([]);
      flash('Shared with everyone');
      load();
    } catch (err) { setError(parseApiError(err, 'Could not share with everyone')); }
  };

  const unshare = async (grant) => {
    try {
      await companyDocsAPI.unshare(grant.id);
      setShares((g) => g.filter((x) => x.id !== grant.id));
      flash('Access removed');
      load();
    } catch (err) { setError(parseApiError(err, 'Could not remove access')); }
  };

  // Group for display so a long library stays navigable.
  const grouped = docs.reduce((acc, d) => {
    const key = d.category || 'Uncategorised';
    (acc[key] = acc[key] || []).push(d);
    return acc;
  }, {});

  const sharedWith = new Set(shares.map((s) => s.user_id));

  return (
    <div className="page">
      <div className="page-header">
        <h2>📚 Company Documents</h2>
        <button className="btn btn-primary" onClick={showForm ? () => setShowForm(false) : openNew}>
          {showForm ? 'Cancel' : '+ Add document'}
        </button>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!isAdmin && (
        <div className="alert alert-warning">
          Anyone can add a document link. A new link is visible only to you and the
          admin until the admin shares it with the team.
        </div>
      )}

      {showForm && (
        <form className="form-card mb-4" onSubmit={submit}>
          <div className="form-group">
            <label>Link *</label>
            <input type="url" required maxLength={2000} value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://drive.google.com/…" />
            <span className="field-hint">
              Paste the Drive, SharePoint or intranet link. Make sure it is shared
              with the organisation at the source, or colleagues will hit a
              permission wall when they open it.
            </span>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input required maxLength={200} value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Employee Onboarding SOP" />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="">None</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Version</label>
              <input maxLength={30} value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="e.g. v2.1" />
            </div>
            <div className="form-group">
              <label>Document type</label>
              <select value={form.doc_kind} onChange={(e) => setForm({ ...form, doc_kind: e.target.value })}>
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea rows={2} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What this document covers and who should read it" />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => { setShowForm(false); setEditing(null); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add document'}
            </button>
          </div>
        </form>
      )}

      <div className="filter-bar">
        <input type="search" value={search} placeholder="Search documents…"
          onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 240 }} />
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="page-loading">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>📚</div>
          <p className="text-muted">
            {search || category
              ? 'No documents match that filter.'
              : 'Nothing here yet. Use “Add document” to link one.'}
          </p>
        </div>
      ) : (
        Object.keys(grouped).sort().map((group) => (
          <div key={group} className="mb-4">
            <div className="form-section-title">{group} ({grouped[group].length})</div>
            <div className="card">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Document</th><th>Version</th><th>Location</th>
                    {isAdmin ? <th>Shared with</th> : <th>Added by</th>}
                    <th>Updated</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped[group].map((d) => (
                    <tr key={d.id}>
                      <td>
                        <strong>{KIND_ICONS[guessKind(d)] || '📄'} {d.title}</strong>
                        {d.description && (
                          <div className="text-muted" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>
                            {d.description}
                          </div>
                        )}
                        {!isAdmin && d.i_added_it && !d.is_shared_with_me && (
                          <div style={{ fontSize: 11.5, marginTop: 2 }}>
                            <span className="badge badge-orange">Awaiting admin review</span>
                          </div>
                        )}
                      </td>
                      <td>{d.version ? <span className="badge badge-gray">{d.version}</span> : <span className="text-muted">—</span>}</td>
                      <td className="text-muted" style={{ fontSize: 12.5, overflowWrap: 'anywhere' }}>
                        {hostOf(d.url)}
                      </td>
                      {isAdmin ? (
                        <td>
                          <span className={`badge badge-${d.shared_with_count > 0 ? 'blue' : 'gray'}`}>
                            {d.shared_with_count} {d.shared_with_count === 1 ? 'person' : 'people'}
                          </span>
                        </td>
                      ) : (
                        <td>{d.i_added_it ? 'You' : (d.added_by?.full_name || '—')}</td>
                      )}
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(d.updated_at, true)}</td>
                      <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <a className="btn btn-sm btn-primary" href={d.url}
                           target="_blank" rel="noopener noreferrer">Open ↗</a>
                        {isAdmin && (
                          <>
                            <button className="btn btn-sm btn-ghost" onClick={() => openShare(d)}>Share</button>
                            <button className="btn btn-sm btn-ghost" onClick={() => openEdit(d)}>Edit</button>
                            <button className="btn btn-sm btn-danger" onClick={() => remove(d)}>Remove</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {sharing && (
        <div className="modal-overlay" onClick={() => setSharing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Who can see “{sharing.title}”</h3>
              <button className="modal-close" onClick={() => setSharing(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-section-title">Currently shared with</div>
              {shares.length === 0 ? (
                <p className="text-muted">
                  Nobody yet — only you and {sharing.added_by?.full_name || 'whoever added it'} can see it.
                </p>
              ) : (
                <table className="data-table">
                  <tbody>
                    {shares.map((s) => (
                      <tr key={s.id}>
                        <td>
                          {s.user?.full_name || `User #${s.user_id}`}
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            since {fmtDate(s.granted_at, true)}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-sm btn-danger" onClick={() => unshare(s)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="form-section-title">Share with</div>
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 6, padding: 8 }}>
                {people.filter((p) => !sharedWith.has(p.id) && p.id !== user?.id).map((p) => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                    <input type="checkbox" checked={picked.includes(p.id)} onChange={() => togglePick(p.id)} />
                    <span>{p.full_name}</span>
                    <span className="text-muted" style={{ fontSize: 12 }}>{p.role?.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>

              <div className="form-actions">
                <button className="btn btn-ghost" onClick={() => setSharing(null)}>Close</button>
                <button className="btn btn-ghost" onClick={shareEveryone}>Share with everyone</button>
                <button className="btn btn-primary" disabled={picked.length === 0} onClick={saveShares}>
                  Share with {picked.length || 0} {picked.length === 1 ? 'person' : 'people'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
